import {useMemo} from 'react';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
} from '@tinct/web-components';
import {EmbeddedPage} from '@tinct/core-api-models';

export type VariantPagesViewProps = {
  pages: EmbeddedPage[];
  remainingQuota: number;
  totalPages: number;
};

export function VariantPagesView({
  pages,
  remainingQuota,
}: VariantPagesViewProps) {
  const sortedPages = useMemo(() => {
    return [...pages]
      .filter((p) => p.status === 'ready')
      .sort((a, b) => {
        const scoreA = a.lead?.company?.enrichmentScore ?? 0;
        const scoreB = b.lead?.company?.enrichmentScore ?? 0;
        return scoreB - scoreA;
      });
  }, [pages]);

  return (
    <div className="flex flex-col rounded-xl border">
      {/* Header */}
      <div className="grid grid-cols-[1fr_auto] items-center gap-4 border-b px-4 py-2 text-xs font-medium text-muted-foreground">
        <span>Company</span>
        <span className="w-24 text-center">Current plan</span>
      </div>

      {/* Rows */}
      {sortedPages.map((page, index) => {
        const company = page.lead?.company;
        const companyName = company?.name ?? 'Unknown';
        const companyInitial = companyName.charAt(0).toUpperCase();
        const logoUrl = company?.logo?.url;
        const isPublishable = index < remainingQuota;

        return (
          <div
            key={page.id}
            className="grid grid-cols-[1fr_auto] items-center gap-4 border-b px-4 py-2.5 last:border-b-0"
          >
            {/* Company */}
            <div className="flex items-center gap-2.5">
              <Avatar className="size-7 rounded-md">
                {logoUrl && <AvatarImage src={logoUrl} alt={companyName} />}
                <AvatarFallback className="rounded-md text-xs">{companyInitial}</AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium">{companyName}</span>
            </div>

            {/* Current plan */}
            <div className="flex w-24 justify-center">
              {isPublishable ? (
                <Badge className="rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-50">
                  Publishable
                </Badge>
              ) : (
                <span className="text-xs text-muted-foreground">Locked</span>
              )}
            </div>
          </div>
        );
      })}

      {sortedPages.length === 0 && (
        <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
          No page variants available
        </div>
      )}
    </div>
  );
}
